import React, { useState, useEffect } from 'react';
import { Calendar, Clock, User, Briefcase, MapPin, Phone, Video, X, Users } from 'lucide-react';
import { supabase } from '../integrations/supabase/client';
import { useToast } from '../hooks/use-toast';
import { toUTC, toDateTimeLocal, nowInSaoPaulo } from '../utils/timezone';

interface CandidatoRH {
  id: string;
  nome: string;
  email: string;
  vaga_id?: string | null;
  vaga?: { titulo: string } | null;
}

interface VagaRH {
  id: string;
  titulo: string;
  status: string;
}

interface NovaEntrevistaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  preSelectedDate?: string;
  editingEntrevista?: any; // Entrevista sendo editada
  candidatoPredefinido?: {
    id: string;
    nome: string;
    email: string;
    vaga_id?: string | null;
  };
}

const NovaEntrevistaModal: React.FC<NovaEntrevistaModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  preSelectedDate,
  editingEntrevista,
  candidatoPredefinido
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [candidatos, setCandidatos] = useState<CandidatoRH[]>([]);
  const [vagas, setVagas] = useState<VagaRH[]>([]);
  const [slotsDisponiveis, setSlotsDisponiveis] = useState<any[]>([]);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [formData, setFormData] = useState({
    candidato_id: '',
    vaga_id: '',
    data_entrevista: '',
    tipo_entrevista: 'presencial',
    modalidade_entrevista: 'individual' as 'individual' | 'coletiva',
    titulo_entrevista_coletiva: '',
    numero_vagas: 1,
    link_reuniao: '',
    observacoes: '',
    duracao_minutos: 60
  });

  // Configurar dados iniciais quando candidato é predefinido
  useEffect(() => {
    if (candidatoPredefinido) {
      setFormData(prev => ({
        ...prev,
        candidato_id: candidatoPredefinido.id,
        vaga_id: candidatoPredefinido.vaga_id || '',
        modalidade_entrevista: 'individual'
      }));
    }
  }, [candidatoPredefinido]);

  useEffect(() => {
    if (isOpen) {
      carregarDados();
    }
  }, [isOpen]);

  useEffect(() => {
    if (editingEntrevista) {
      setFormData({
        candidato_id: editingEntrevista.candidato_id || '',
        vaga_id: editingEntrevista.vaga_id || '',
        data_entrevista: toDateTimeLocal(editingEntrevista.data_entrevista) || '',
        tipo_entrevista: editingEntrevista.tipo_entrevista || 'presencial',
        modalidade_entrevista: 'individual',
        titulo_entrevista_coletiva: '',
        numero_vagas: 1,
        link_reuniao: editingEntrevista.link_reuniao || '',
        observacoes: editingEntrevista.observacoes || '',
        duracao_minutos: editingEntrevista.duracao_minutos || 60
      });
    } else if (preSelectedDate) {
      setFormData(prev => ({
        ...prev,
        data_entrevista: preSelectedDate
      }));
    }
  }, [editingEntrevista, preSelectedDate]);

  const carregarDados = async () => {
    try {
      // Carregar candidatos
      const { data: candidatosData, error: candidatosError } = await supabase
        .from('candidatos_rh')
        .select(`
          id,
          nome,
          email,
          vaga_id,
          vaga:vagas_rh(titulo)
        `)
        .order('nome');

      if (candidatosError) throw candidatosError;
      setCandidatos(candidatosData || []);

      // Carregar vagas ativas
      const { data: vagasData, error: vagasError } = await supabase
        .from('vagas_rh')
        .select('id, titulo, status')
        .eq('status', 'ativa')
        .order('titulo');

      if (vagasError) throw vagasError;
      setVagas(vagasData || []);

    } catch (error) {
      console.error('Erro ao carregar dados:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar dados necessários",
        variant: "destructive"
      });
    }
  };

  const buscarSlotsDisponiveis = async () => {
    setLoadingSlots(true);
    try {
      // Gerar horários simples: próximos 14 dias, horário comercial
      const slots = [];
      const hoje = nowInSaoPaulo();
      
      for (let i = 1; i <= 14; i++) {
        const data = new Date(hoje);
        data.setDate(data.getDate() + i);
        
        // Pular fins de semana
        if (data.getDay() === 0 || data.getDay() === 6) continue;
        
        // Horários: 9h às 17h
        for (let hora = 9; hora <= 16; hora++) {
          const dataHora = new Date(data);
          dataHora.setHours(hora, 0, 0, 0);
          
          // Formato datetime-local em São Paulo
          const dataHoraLocal = `${dataHora.getFullYear()}-${String(dataHora.getMonth() + 1).padStart(2, '0')}-${String(dataHora.getDate()).padStart(2, '0')}T${String(hora).padStart(2, '0')}:00`;
          
          slots.push({
            data_hora: dataHoraLocal,
            label: `${dataHora.toLocaleDateString('pt-BR')} às ${hora}:00`
          });
        }
      }
      
      setSlotsDisponiveis(slots);
    } catch (error) {
      console.error('Erro ao gerar slots:', error);
      toast({
        title: "Erro",
        description: "Erro ao gerar horários disponíveis",
        variant: "destructive"
      });
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Converter datetime-local para UTC usando timezone de São Paulo
    const prepareDateTime = (dateTimeString: string) => {
      console.log('=== DEBUG FUSO HORÁRIO ===');
      console.log('Input datetime-local (São Paulo):', dateTimeString);
      
      // Converter para UTC usando a função utilitária
      const utcValue = toUTC(dateTimeString);
      console.log('Valor UTC para Supabase:', utcValue);
      
      return utcValue;
    };

    // Validação baseada no tipo de entrevista
    if (formData.modalidade_entrevista === 'individual') {
      if (!formData.candidato_id || !formData.vaga_id || !formData.data_entrevista) {
        toast({
          title: "Erro",
          description: "Preencha todos os campos obrigatórios",
          variant: "destructive"
        });
        return;
      }
    } else {
      if (!formData.vaga_id || !formData.data_entrevista || !formData.titulo_entrevista_coletiva) {
        toast({
          title: "Erro",
          description: "Preencha todos os campos obrigatórios para a entrevista coletiva",
          variant: "destructive"
        });
        return;
      }
    }

    // Validar se já existe entrevista na mesma data/horário
    const dataEntrevistaUTC = prepareDateTime(formData.data_entrevista);
    
    try {
      const { data: entrevistasExistentes, error: errorValidacao } = await supabase
        .from('entrevistas_rh')
        .select('id, candidato_id')
        .eq('data_entrevista', dataEntrevistaUTC)
        .neq('status', 'cancelada'); // Ignorar entrevistas canceladas

      if (errorValidacao) throw errorValidacao;

      // Se estamos editando, excluir a própria entrevista da verificação
      const conflitos = editingEntrevista 
        ? entrevistasExistentes?.filter(e => e.id !== editingEntrevista.id) || []
        : entrevistasExistentes || [];

      if (conflitos.length > 0) {
        toast({
          title: "Horário Indisponível",
          description: "Já existe uma entrevista agendada para este horário. Escolha outro horário.",
          variant: "destructive"
        });
        return;
      }
    } catch (error) {
      console.error('Erro ao validar conflito de horário:', error);
    }

    setLoading(true);
    try {
      const baseData = {
        vaga_id: formData.vaga_id,
        data_entrevista: prepareDateTime(formData.data_entrevista),
        entrevistador_nome: 'Aleska Duarte',
        entrevistador_email: 'aleska@foxvendas.com.br',
        tipo_entrevista: formData.tipo_entrevista,
        link_reuniao: formData.link_reuniao || null,
        duracao_minutos: formData.duracao_minutos,
        status: 'agendada'
      };

      let entrevistaData: any = { ...baseData };

      if (formData.modalidade_entrevista === 'individual') {
        entrevistaData.candidato_id = formData.candidato_id;
        entrevistaData.observacoes = formData.observacoes || null;
      } else {
        // Para entrevistas coletivas
        const observacoesColetiva = `ENTREVISTA COLETIVA: ${formData.titulo_entrevista_coletiva}${formData.numero_vagas > 1 ? ` (${formData.numero_vagas} vagas)` : ''}${formData.observacoes ? '\n\n' + formData.observacoes : ''}`;
        entrevistaData.observacoes = observacoesColetiva;
      }

      if (editingEntrevista) {
        // Modo edição - UPDATE
        const { error } = await supabase
          .from('entrevistas_rh')
          .update(entrevistaData)
          .eq('id', editingEntrevista.id);

        if (error) throw error;

        toast({
          title: "Sucesso",
          description: "Entrevista atualizada com sucesso!"
        });
      } else {
        // Modo criação - INSERT
        if (formData.modalidade_entrevista === 'individual') {
          const { error } = await supabase
            .from('entrevistas_rh')
            .insert({
              ...entrevistaData,
              candidato_id: formData.candidato_id
            });

          if (error) throw error;

          // Criar evento no Google Calendar se integração estiver ativa
          const accessToken = localStorage.getItem('google_calendar_access_token');
          if (accessToken) {
            try {
              // Buscar o ID da entrevista recém-criada
              const { data: entrevistaCreated } = await supabase
                .from('entrevistas_rh')
                .select('id')
                .eq('candidato_id', formData.candidato_id)
                .eq('vaga_id', formData.vaga_id)
                .eq('data_entrevista', prepareDateTime(formData.data_entrevista))
                .single();

              if (entrevistaCreated) {
                await supabase.functions.invoke('google-calendar-sync', {
                  body: {
                    action: 'create_event',
                    entrevistaId: entrevistaCreated.id,
                    accessToken
                  }
                });
              }
            } catch (calendarError) {
              console.warn('Erro ao criar evento no Google Calendar:', calendarError);
            }
          }

          // Atualizar status do candidato
          await supabase
            .from('candidatos_rh')
            .update({ status: 'entrevista_marcada' })
            .eq('id', formData.candidato_id);

        } else {
          // Entrevista coletiva - criar candidato fictício
          const { data: candidatoTemp, error: candidatoError } = await supabase
            .from('candidatos_rh')
            .insert({
              nome: `Entrevista Coletiva - ${formData.titulo_entrevista_coletiva}`,
              email: 'entrevista.coletiva@foxvendas.com',
              telefone: '(00) 00000-0000',
              vaga_id: formData.vaga_id,
              status: 'entrevista_marcada',
              tipo_cadastro: 'candidato'
            })
            .select()
            .single();

          if (candidatoError) throw candidatoError;

          // Inserir a entrevista
          const { error } = await supabase
            .from('entrevistas_rh')
            .insert({
              ...entrevistaData,
              candidato_id: candidatoTemp.id
            });

          if (error) throw error;

          // Criar evento no Google Calendar se integração estiver ativa
          const accessToken = localStorage.getItem('google_calendar_access_token');
          if (accessToken && candidatoTemp.id) {
            try {
              await supabase.functions.invoke('google-calendar-sync', {
                body: {
                  action: 'create_event',
                  entrevistaId: candidatoTemp.id,
                  accessToken
                }
              });
            } catch (calendarError) {
              console.warn('Erro ao criar evento no Google Calendar:', calendarError);
            }
          }
        }

        toast({
          title: "Sucesso",
          description: formData.modalidade_entrevista === 'coletiva' 
            ? "Entrevista coletiva agendada com sucesso!"
            : "Entrevista agendada com sucesso!"
        });
      }

      onSuccess();
      onClose();
      resetForm();
    } catch (error) {
      console.error('Erro ao processar entrevista:', error);
      toast({
        title: "Erro",
        description: editingEntrevista ? "Erro ao atualizar entrevista" : "Erro ao agendar entrevista",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      candidato_id: '',
      vaga_id: '',
      data_entrevista: '',
      tipo_entrevista: 'presencial',
      modalidade_entrevista: 'individual',
      titulo_entrevista_coletiva: '',
      numero_vagas: 1,
      link_reuniao: '',
      observacoes: '',
      duracao_minutos: 60
    });
    setSlotsDisponiveis([]);
  };

  const formatarSlot = (slot: any) => {
    return slot.label;
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg w-full max-w-4xl max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-xl font-bold text-gray-900 flex items-center">
              <Calendar className="w-6 h-6 text-orange-500 mr-2" />
              {editingEntrevista ? 'Editar Entrevista' : 'Nova Entrevista'}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Modalidade da Entrevista */}
            <div className="space-y-3">
              <label className="text-sm font-medium text-foreground">
                Modalidade da Entrevista
              </label>
              <div className="flex gap-4">
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="modalidade"
                    value="individual"
                    checked={formData.modalidade_entrevista === 'individual'}
                    onChange={(e) => setFormData({...formData, modalidade_entrevista: e.target.value as 'individual' | 'coletiva'})}
                    disabled={!!candidatoPredefinido} // Desabilitar se candidato é predefinido
                    className="text-primary focus:ring-primary"
                  />
                  <User className="h-4 w-4" />
                  <span className="text-sm">Individual</span>
                </label>
                <label className="flex items-center gap-2">
                  <input
                    type="radio"
                    name="modalidade"
                    value="coletiva"
                    checked={formData.modalidade_entrevista === 'coletiva'}
                    onChange={(e) => setFormData({...formData, modalidade_entrevista: e.target.value as 'individual' | 'coletiva'})}
                    disabled={!!candidatoPredefinido} // Desabilitar se candidato é predefinido
                    className="text-primary focus:ring-primary"
                  />
                  <Users className="h-4 w-4" />
                  <span className="text-sm">Coletiva</span>
                </label>
              </div>
            </div>

            {/* Campos específicos para entrevista coletiva */}
            {formData.modalidade_entrevista === 'coletiva' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Título da Entrevista Coletiva *
                  </label>
                  <input
                    type="text"
                    value={formData.titulo_entrevista_coletiva}
                    onChange={(e) => setFormData({ ...formData, titulo_entrevista_coletiva: e.target.value })}
                    placeholder="Ex: Processo Seletivo Corretor de Imóveis"
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                    required={formData.modalidade_entrevista === 'coletiva'}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Número de Vagas
                  </label>
                  <input
                    type="number"
                    min="1"
                    value={formData.numero_vagas}
                    onChange={(e) => setFormData({ ...formData, numero_vagas: parseInt(e.target.value) || 1 })}
                    className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Seleção de Candidato (apenas para entrevistas individuais) */}
              {formData.modalidade_entrevista === 'individual' && !candidatoPredefinido && (
                <div className="space-y-2">
                  <label htmlFor="candidato_id" className="text-sm font-medium text-foreground flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Candidato
                  </label>
                  <select
                    id="candidato_id"
                    value={formData.candidato_id}
                    onChange={(e) => setFormData({...formData, candidato_id: e.target.value})}
                    required
                    className="w-full px-3 py-2 border border-border rounded-md bg-background text-foreground focus:ring-2 focus:ring-primary focus:border-transparent"
                  >
                    <option value="">Selecione um candidato</option>
                    {candidatos.map((candidato) => (
                      <option key={candidato.id} value={candidato.id}>
                        {candidato.nome} - {candidato.email}
                        {candidato.vaga && ` (${candidato.vaga.titulo})`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* Mostrar candidato predefinido */}
              {candidatoPredefinido && (
                <div className="space-y-2">
                  <label className="text-sm font-medium text-foreground flex items-center gap-2">
                    <User className="h-4 w-4" />
                    Candidato
                  </label>
                  <div className="w-full px-3 py-2 border border-border rounded-md bg-muted/50 text-foreground">
                    {candidatoPredefinido.nome} - {candidatoPredefinido.email}
                  </div>
                </div>
              )}

              {/* Vaga */}
              <div className={formData.modalidade_entrevista === 'coletiva' ? 'md:col-span-2' : ''}>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  <Briefcase className="w-4 h-4 inline mr-1" />
                  Vaga *
                </label>
                <select
                  value={formData.vaga_id}
                  onChange={(e) => setFormData({ ...formData, vaga_id: e.target.value })}
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                  required
                >
                  <option value="">Selecione uma vaga</option>
                  {vagas.map(vaga => (
                    <option key={vaga.id} value={vaga.id}>
                      {vaga.titulo}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Horários Disponíveis */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <label className="block text-sm font-medium text-gray-700">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Horário da Entrevista *
                </label>
                <button
                  type="button"
                  onClick={buscarSlotsDisponiveis}
                  disabled={loadingSlots}
                  className="text-orange-600 hover:text-orange-700 text-sm flex items-center"
                >
                  {loadingSlots ? 'Carregando...' : 'Buscar Horários Disponíveis'}
                </button>
              </div>
              
              {slotsDisponiveis.length > 0 ? (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-4">
                  {slotsDisponiveis.map((slot, index) => (
                    <button
                      key={index}
                      type="button"
                      onClick={() => setFormData({ ...formData, data_entrevista: slot.data_hora })}
                      className={`p-2 text-sm border rounded-lg transition-colors ${
                        formData.data_entrevista === slot.data_hora
                          ? 'bg-orange-100 border-orange-300 text-orange-700'
                          : 'border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {formatarSlot(slot)}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="text-center py-4 text-gray-500 text-sm">
                  Clique em "Buscar Horários Disponíveis" para ver os slots
                </div>
              )}

              <input
                type="datetime-local"
                value={formData.data_entrevista}
                onChange={(e) => setFormData({ ...formData, data_entrevista: e.target.value })}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                required
              />
            </div>

            {/* Tipo de Entrevista */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Entrevista *
              </label>
              <div className="grid grid-cols-3 gap-4">
                {[
                  { value: 'presencial', label: 'Presencial', icon: MapPin },
                  { value: 'telefone', label: 'Telefone', icon: Phone },
                  { value: 'video', label: 'Vídeo', icon: Video }
                ].map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData({ ...formData, tipo_entrevista: value })}
                    className={`p-3 border rounded-lg flex items-center justify-center transition-colors ${
                      formData.tipo_entrevista === value
                        ? 'bg-orange-100 border-orange-300 text-orange-700'
                        : 'border-gray-300 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className="w-4 h-4 mr-2" />
                    {label}
                  </button>
                ))}
              </div>
            </div>

            {/* Link da Reunião */}
            {formData.tipo_entrevista === 'video' && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Link da Reunião
                </label>
                <input
                  type="url"
                  value={formData.link_reuniao}
                  onChange={(e) => setFormData({ ...formData, link_reuniao: e.target.value })}
                  placeholder="https://meet.google.com/..."
                  className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                />
              </div>
            )}

            {/* Observações */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Observações
              </label>
              <textarea
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                rows={3}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 focus:border-transparent"
                placeholder="Informações adicionais sobre a entrevista..."
              />
            </div>

            {/* Botões */}
            <div className="flex justify-end space-x-3 pt-6 border-t">
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-6 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    {editingEntrevista ? 'Atualizando...' : 'Agendando...'}
                  </>
                ) : (
                  <>
                    <Calendar className="w-4 h-4 mr-2" />
                    {editingEntrevista ? 'Atualizar Entrevista' : 'Agendar Entrevista'}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default NovaEntrevistaModal;